import React, { useState, useEffect } from 'react';
import { Trophy, Medal, Award, TrendingUp, User, RefreshCw } from 'lucide-react';

interface LeaderboardUser {
  ahn_id: string;
  name: string;
  urja_balance: string;
  ahant_balance: string;
  rank: number;
  created_at: string;
}

interface LeaderboardProps {
  token: string;
  currentUserId?: string;
}

const Leaderboard: React.FC<LeaderboardProps> = ({ token, currentUserId }) => {
  const [leaderboard, setLeaderboard] = useState<LeaderboardUser[]>([]);
  const [userRank, setUserRank] = useState<LeaderboardUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const API_URL = process.env.REACT_APP_API_URL || '/api';

  useEffect(() => {
    fetchLeaderboard();
    if (token) {
      fetchUserRank();
    }
  }, [token]);

  const fetchLeaderboard = async () => {
    try {
      const response = await fetch(`${API_URL}/leaderboard?limit=50`);
      
      const data = await response.json();
      
      if (data.success) {
        setLeaderboard(data.data.leaderboard);
        setError('');
      } else {
        setError(data.message || 'Failed to fetch leaderboard');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Leaderboard fetch error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchUserRank = async () => {
    try {
      const response = await fetch(`${API_URL}/leaderboard/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setUserRank(data.data);
      }
    } catch (err) {
      console.error('User rank fetch error:', err);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchLeaderboard();
    if (token) {
      await fetchUserRank();
    }
  };

  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Trophy className="w-6 h-6 text-yellow-400" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-gray-400" />;
    if (rank === 3) return <Award className="w-6 h-6 text-amber-600" />;
    return <span className="w-6 text-center font-bold text-gray-400">#{rank}</span>;
  };

  const getRankBadgeColor = (rank: number) => {
    if (rank === 1) return 'bg-gradient-to-r from-yellow-400 to-yellow-600';
    if (rank === 2) return 'bg-gradient-to-r from-gray-300 to-gray-500';
    if (rank === 3) return 'bg-gradient-to-r from-amber-600 to-amber-800';
    return 'bg-gradient-to-r from-blue-500 to-blue-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900">
        <div className="text-white text-xl">Loading leaderboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 p-4">
      <div className="max-w-4xl mx-auto py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <TrendingUp className="w-12 h-12 text-yellow-400 mr-3" />
            <h1 className="text-4xl font-bold text-white">URJA Leaderboard</h1>
          </div>
          <p className="text-gray-300">Top miners in the AhantraVyuh network</p>
          
          {/* Refresh Button */}
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="mt-4 px-6 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg transition-all flex items-center space-x-2 mx-auto disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
          </button>
        </div>

        {/* User's Rank Card (if logged in) */}
        {userRank && (
          <div className={`${getRankBadgeColor(userRank.rank)} rounded-lg p-6 mb-6 shadow-2xl`}>
            <div className="flex items-center justify-between text-white">
              <div className="flex items-center space-x-4">
                <div className="bg-white bg-opacity-20 p-3 rounded-full">
                  <User className="w-8 h-8" />
                </div>
                <div>
                  <p className="text-sm opacity-90">Your Rank</p>
                  <p className="text-2xl font-bold">#{userRank.rank}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm opacity-90">Your URJA</p>
                <p className="text-3xl font-bold">{parseFloat(userRank.urja_balance).toFixed(2)}</p>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-500 bg-opacity-20 border border-red-500 text-red-200 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Leaderboard Table */}
        <div className="bg-white bg-opacity-10 backdrop-blur-lg rounded-lg shadow-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white bg-opacity-20">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-white">Rank</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-white">Name</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-white">URJA Balance</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-white">AHANT Balance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white divide-opacity-10">
                {leaderboard.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-8 text-center text-gray-400">
                      No users found. Be the first to mine URJA!
                    </td>
                  </tr>
                ) : (
                  leaderboard.map((user) => (
                    <tr 
                      key={user.ahn_id}
                      className={`hover:bg-white hover:bg-opacity-5 transition-colors ${
                        user.ahn_id === currentUserId ? 'bg-blue-500 bg-opacity-20' : ''
                      }`}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          {getRankIcon(user.rank)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white font-bold">
                            {user.name.charAt(0).toUpperCase()}
                          </div>
                          <span className="text-white font-medium">{user.name}</span>
                          {user.ahn_id === currentUserId && (
                            <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded">You</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className="text-yellow-400 font-bold text-lg">
                          {parseFloat(user.urja_balance).toFixed(2)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className="text-green-400 font-semibold">
                          {parseFloat(user.ahant_balance).toFixed(2)}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer Stats */}
        <div className="mt-6 text-center text-gray-400 text-sm">
          Showing top {leaderboard.length} miners
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;